import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { env } from '../lib/env';

type Portal = 'candidate' | 'employer' | 'admin';

const AUTH_CONFIGURED = Boolean(import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID);

interface UsePortalLoginOptions {
  portal: Portal;
  returnTo: string;
}

interface TriggerOptions {
  screenHint?: 'login' | 'signup';
  prompt?: 'login' | 'none';
}

export function usePortalLogin({ portal, returnTo }: UsePortalLoginOptions) {
  const { login } = useAuth();
  const [isPending, setIsPending] = useState(false);

  const trigger = async ({ screenHint, prompt, connection }: TriggerOptions & { connection?: string } = {}) => {
    if (!AUTH_CONFIGURED) {
      toast.error('Auth0 is not configured. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.');
      return;
    }

    setIsPending(true);
    try {
      await login({
        appState: { returnTo, portal },
        authorizationParams: {
          portal,
          ...(connection ? { connection } : {}),
          ...(screenHint ? { screen_hint: screenHint } : {}),
          ...(prompt ? { prompt } : {}),
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to start secure login';
      toast.error(message);
    } finally {
      setIsPending(false);
    }
  };

  return {
    authConfigured: AUTH_CONFIGURED,
    isPending,
    login: () => trigger({ screenHint: 'login' }),
    register: () => trigger({ screenHint: 'signup' }),
    forceReauth: () => trigger({ prompt: 'login' }),
    loginWithConnection: (connection?: string) => trigger({ screenHint: 'login', connection }),
  };
}

export const portalLoginConfigured = AUTH_CONFIGURED;
export const socialConnections = env.socialConnections;
